import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  ACCOUNT_STATUS_STYLES,
  StatusBadge,
  SUBSCRIPTION_STATUS_STYLES,
} from "@/components/admin/AccountTable";
import {
  AccountInfo,
  AuditList,
  BillingPanel,
  UsageMonthPanel,
  UsagePanel,
} from "@/components/admin/AccountDetailSections";
import { AccountEditFields } from "@/components/admin/AccountEditFields";

const formFrom = (data) => ({
  company_name: data.name || "",
  contact_email: data.user?.email || "",
  phone: data.phone || "",
  country: data.country || "",
  internal_note: data.internal_note || "",
  plan_code: data.subscription?.plan_code || "imdash_pro",
  subscription_status: data.subscription?.status || "TRIAL",
  monthly_price: data.subscription?.monthly_price ?? "",
  trial_ends_at: (data.subscription?.trial_ends_at || "").slice(0, 10),
  subscription_ends_at: (data.subscription?.ends_at || "").slice(0, 10),
  limit_scans: data.usage_limits?.scans_per_month ?? "",
  limit_ai_analyses: data.usage_limits?.ai_analyses_per_month ?? "",
  limit_refreshes: data.usage_limits?.refreshes_per_month ?? "",
});

const LIMIT_FIELDS = [
  ["limit_scans", "scans_per_month"],
  ["limit_ai_analyses", "ai_analyses_per_month"],
  ["limit_refreshes", "refreshes_per_month"],
];

export const AccountDetailDialog = ({ accountId, plans, onClose, onChanged, onImpersonate }) => {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [link, setLink] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/accounts/${accountId}`);
      setDetail(data);
      setForm(formFrom(data));
    } catch (e) {
      setError(apiError(e));
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const run = async (fn, message) => {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (result?.invite_link) setLink(result.invite_link);
      if (result?.reset_link) setLink(result.reset_link);
      toast.success(message);
      await load();
      onChanged();
      return true;
    } catch (e) {
      setError(apiError(e));
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      const usage_limits = {};
      LIMIT_FIELDS.forEach(([field, key]) => {
        if (form[field] !== "" && form[field] != null) usage_limits[key] = Number(form[field]);
      });
      const payload = {
        ...form,
        contact_email: form.contact_email || null,
        monthly_price: form.monthly_price === "" ? null : Number(form.monthly_price),
        clear_custom_price: form.monthly_price === "",
        trial_ends_at: form.trial_ends_at || null,
        subscription_ends_at: form.subscription_ends_at || null,
        usage_limits,
      };
      const { data } = await api.patch(`/admin/accounts/${accountId}`, payload);
      return data;
    }, "Klantgegevens bijgewerkt");

  const grant = (metric, amount) =>
    run(
      async () =>
        (await api.post(`/admin/accounts/${accountId}/usage-grant`, { metric, amount })).data,
      "Extra ruimte toegekend voor deze maand",
    );

  const paymentLink = () =>
    run(async () => {
      const { data } = await api.post(`/admin/accounts/${accountId}/payment-link`, {
        origin_url: window.location.origin,
      });
      setLink(data.checkout_url);
      return data;
    }, "Betaallink aangemaakt");

  const post = (path, message) =>
    run(async () => (await api.post(`/admin/accounts/${accountId}/${path}`, {})).data, message);

  const setStatus = (status) =>
    run(
      async () => (await api.post(`/admin/accounts/${accountId}/status`, { status })).data,
      `Account ${status === "ACTIVE" ? "geactiveerd" : "bijgewerkt"}`,
    );

  const remove = async () => {
    if (!window.confirm("Account en alle bijbehorende klantdata definitief verwijderen?")) return;
    const ok = await run(
      async () => (await api.delete(`/admin/accounts/${accountId}`)).data,
      "Account verwijderd",
    );
    if (ok) onClose();
  };

  if (!detail)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="panel p-5 text-[13px] text-slate-500">Klantgegevens laden…</div>
      </div>
    );

  const pricing = detail.pricing || {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4">
      <div className="panel w-full max-w-4xl p-5" data-testid="admin-account-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{detail.name}</h2>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={detail.account_status} styles={ACCOUNT_STATUS_STYLES} />
              <StatusBadge
                status={detail.subscription?.status}
                styles={SUBSCRIPTION_STATUS_STYLES}
              />
              {detail.is_test_account && (
                <span className="chip bg-slate-100 text-slate-600">TESTACCOUNT</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            data-testid="admin-detail-close"
          >
            Sluiten
          </button>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <AccountInfo detail={detail} pricing={pricing} />
          <UsagePanel usage={detail.usage} />
        </div>

        <div className="mt-4 grid gap-4 border-t border-slate-200 pt-4 lg:grid-cols-2">
          <UsageMonthPanel usage={detail.usage_month} onGrant={grant} busy={busy} />
          <BillingPanel
            billing={detail.billing}
            subscription={detail.subscription}
            onPaymentLink={paymentLink}
            busy={busy}
          />
        </div>

        <div className="mt-5 border-t border-slate-200 pt-4">
          <h3 className="label-xs">Gegevens en abonnement wijzigen</h3>
          <AccountEditFields
            form={form}
            set={set}
            plans={plans}
            planLimits={detail.usage_month?.limits}
          />
          {error && (
            <div
              className="mt-3 rounded-sm border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-800"
              data-testid="admin-detail-error"
            >
              {error}
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={save}
              className="btn-primary"
              data-testid="admin-detail-save"
            >
              Opslaan
            </button>
            {detail.account_status === "ACTIVE" ? (
              <button
                type="button"
                disabled={busy || detail.is_master}
                onClick={() => setStatus("PAUSED")}
                className="btn-secondary"
                data-testid="admin-detail-pause"
              >
                Pauzeren
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => setStatus("ACTIVE")}
                className="btn-secondary"
                data-testid="admin-detail-activate"
              >
                Activeren
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => post("invite", "Uitnodiging opnieuw verstuurd")}
              className="btn-secondary"
              data-testid="admin-detail-invite"
            >
              Uitnodiging opnieuw sturen
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => post("password-reset", "Wachtwoordherstel verstuurd")}
              className="btn-secondary"
              data-testid="admin-detail-reset"
            >
              Wachtwoord resetten
            </button>
            {!detail.is_master && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onImpersonate(detail)}
                  className="btn-secondary"
                  data-testid="admin-detail-impersonate"
                >
                  Account openen
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={remove}
                  className="btn-secondary text-red-700"
                  data-testid="admin-detail-delete"
                >
                  Verwijderen
                </button>
              </>
            )}
          </div>
          {link && (
            <div className="mt-3 rounded-sm border border-blue-200 bg-blue-50 p-2.5">
              <div className="label-xs">
                Link (kopieer en stuur zelf indien de e-mail niet aankomt)
              </div>
              <div className="mono mt-1 break-all text-[11px] text-slate-700">{link}</div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(link);
                  toast.success("Link gekopieerd");
                }}
                className="btn-ghost mt-1"
                data-testid="admin-copy-link"
              >
                Link kopiëren
              </button>
            </div>
          )}
        </div>

        <AuditList entries={detail.audit} />
      </div>
    </div>
  );
};
